import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertiesApi } from '../../services/adminApi';
import '../../styles/AdminPages.css';

const InvestorPropertiesPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const propertiesResponse = await propertiesApi.getAll({ pageSize: 100 });
        const properties = propertiesResponse?.data?.[0] || propertiesResponse?.data || [];
        
        const investorDataPromises = properties.map(async (property) => {
          try {
            const response = await fetch(`https://api.tannit.uk/Property/GetPropertyInvestors/${property.propertyId}`);
            const result = await response.json();
            const investorInfo = result?.data?.[0]?.[0] || {};
            return {
              propertyId: property.propertyId,
              propertyName: property.name || `Property ${property.propertyId}`,
              propertyCity: property.city || '-',
              numberOfInvestors: investorInfo.numberOfInvestors || 0,
              propertyPrice: investorInfo.propertyPrice || property.price || 0,
              totalTannitInvested: investorInfo.totalTannitInvested || 0,
              percentageFunded: investorInfo.propertyPrice > 0 
                ? ((investorInfo.totalTannitInvested / investorInfo.propertyPrice) * 100).toFixed(1)
                : 0
            };
          } catch (err) {
            return {
              propertyId: property.propertyId,
              propertyName: property.name || `Property ${property.propertyId}`,
              propertyCity: property.city || '-',
              numberOfInvestors: 0,
              propertyPrice: property.price || 0,
              totalTannitInvested: 0,
              percentageFunded: 0
            };
          }
        });

        const investorData = await Promise.all(investorDataPromises);
        setData(investorData.filter(item => item.numberOfInvestors > 0));
        setError(null);
      } catch (err) {
        console.error('Failed to fetch investor properties:', err);
        setError('Failed to load investor properties data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleViewProperty = (propertyId) => {
    navigate(`/admin/properties/view/${propertyId}`);
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#475569',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#e2e8f0'}
            onMouseOut={(e) => e.currentTarget.style.background = '#f1f5f9'}
          >
            <span style={{ fontSize: '16px' }}>←</span> Back
          </button>
          <div>
            <h1>Investor Properties</h1>
            <p>Overview of investor participation per property</p>
          </div>
        </div>
      </div>

      <div className="admin-table-card">
        {error && (
          <div className="admin-form-error" style={{ margin: '20px', borderRadius: '8px' }}>
            {error}
          </div>
        )}
        
        <table className="admin-table">
          <thead>
            <tr>
              <th>Property ID</th>
              <th>Property Name</th>
              <th>City</th>
              <th>Property Price</th>
              <th>Investors</th>
              <th>Total Invested</th>
              <th>% Funded</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="admin-loading-row">
                <td colSpan="8">
                  <div className="admin-table-loading">
                    <div className="admin-spinner"></div>
                    <span>Loading...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="8" className="admin-empty">
                  No properties with investors found
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr key={item.propertyId}>
                  <td>{item.propertyId}</td>
                  <td>{item.propertyName}</td>
                  <td>{item.propertyCity}</td>
                  <td>£{item.propertyPrice.toLocaleString()}</td>
                  <td>{item.numberOfInvestors}</td>
                  <td>£{item.totalTannitInvested.toLocaleString()}</td>
                  <td>
                    <div className="progress" style={{ height: '20px', minWidth: '80px' }}>
                      <div 
                        className="progress-bar bg-success" 
                        style={{ width: `${Math.min(item.percentageFunded, 100)}%` }}
                      >
                        {item.percentageFunded}%
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button 
                        className="admin-btn-sm view"
                        onClick={() => handleViewProperty(item.propertyId)}
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvestorPropertiesPage;
